(ns ui.view.enrich.fly
  "A robot that flies from where an AI enrichment job was started into the top
   bar's process list, so the user sees where the job went.

   The robot grows in place to five times its size, sets off on an arc towards
   the process list button and shrinks back on the way. When it lands on the
   button, the list opens on the new job alone.

   Plain DOM (through ui.dom-globals) and animation frames: the robot lives for
   the flight only, outside the React tree, and aims at the button's position
   on every frame, so it follows the top bar if it moves. With reduced motion
   preferred the list opens without the flight."
  (:require
   [re-frame.core :as rf]
   [ui.dom-globals :as dg]
   [ui.enrich.event :as enrich-event]))

(def target-dom-id
  "The dom id of the top bar's process list button."
  "navbar-processes-button")

(def ^:private duration-ms 2000)
(def ^:private max-scale 5)
;; Shares of the flight: growing in place until hold-until, growing on the way
;; until grow-until, then shrinking down to the button.
(def ^:private hold-until 0.1)
(def ^:private grow-until 0.35)

(defn- ease-out [t] (- 1 (js/Math.pow (- 1 t) 3)))

(defn- ease-in-out
  [t]
  (if (< t 0.5)
    (* 4 t t t)
    (- 1 (/ (js/Math.pow (+ (* -2 t) 2) 3) 2))))

(defn- centre
  [el]
  (let [{:keys [left top width height]} (dg/rect-map el)]
    {:x (+ left (/ width 2))
     :y (+ top (/ height 2))
     :w width}))

(defn- shown
  "`el` when it is on screen."
  [el]
  (when (and el (pos? (:w (centre el)))) el))

(defn- scale-at
  [p]
  (if (< p grow-until)
    (+ 1 (* (dec max-scale) (ease-out (/ p grow-until))))
    (- max-scale (* (dec max-scale)
                    (ease-in-out (/ (- p grow-until) (- 1 grow-until)))))))

(defn- point-at
  "The robot's centre at `p`, on a curve that rises from `from` and sweeps
   across to `to`: a quadratic Bezier with its control point above the start."
  [p from to]
  (let [t (ease-in-out (max 0 (/ (- p hold-until) (- 1 hold-until))))
        u (- 1 t)]
    {:x (+ (* u u (:x from)) (* 2 u t (:x from)) (* t t (:x to)))
     :y (+ (* u u (:y from)) (* 2 u t (:y to)) (* t t (:y to)))}))

(defn- reduced-motion?
  []
  (dg/media-matches? "(prefers-reduced-motion: reduce)"))

(def ^:private sparkles-svg
  "The Lucide `sparkles` icon as inline markup, so the plain-DOM flight shows the
   same icon as the ui.icons sparkles used across the enrichment UI. The flight
   lives outside React, so it cannot mount the react icon component."
  (str "<svg xmlns=\"http://www.w3.org/2000/svg\" width=\"16\" height=\"16\" "
       "viewBox=\"0 0 24 24\" fill=\"none\" stroke=\"currentColor\" stroke-width=\"2\" "
       "stroke-linecap=\"round\" stroke-linejoin=\"round\">"
       "<path d=\"M11.017 2.814a1 1 0 0 1 1.966 0l1.051 5.558a2 2 0 0 0 1.594 1.594l5.558 "
       "1.051a1 1 0 0 1 0 1.966l-5.558 1.051a2 2 0 0 0-1.594 1.594l-1.051 5.558a1 1 0 0 1-1.966 "
       "0l-1.051-5.558a2 2 0 0 0-1.594-1.594l-5.558-1.051a1 1 0 0 1 0-1.966l5.558-1.051a2 2 0 0 "
       "0 1.594-1.594z\"/><path d=\"M20 2v4\"/><path d=\"M22 4h-4\"/>"
       "<circle cx=\"4\" cy=\"20\" r=\"2\"/></svg>"))

(defn- fly!
  [source-el token]
  (let [from  (centre source-el)
        robot (dg/create-element "span")
        start (atom nil)
        to    (atom (centre (dg/element-by-id target-dom-id)))]
    (dg/set-class-name! robot "text-primary-regular")
    (dg/set-inner-html! robot sparkles-svg)
    (doseq [[property value] {"position"       "fixed"
                              "left"           "0"
                              "top"            "0"
                              "z-index"        "9999"
                              "pointer-events" "none"
                              "will-change"    "transform"
                              "display"        "inline-block"
                              "line-height"    "0"}]
      (dg/set-style! robot property value))
    (dg/append-to-body robot)
    (letfn [(frame [now]
              (when-not @start (reset! start now))
              (let [p (min 1 (/ (- now @start) duration-ms))]
                (when-let [target (shown (dg/element-by-id target-dom-id))]
                  (reset! to (centre target)))
                (let [{:keys [x y]} (point-at p from @to)]
                  (dg/set-style! robot "transform"
                                 (str "translate(" x "px, " y "px) translate(-50%, -50%) scale(" (scale-at p) ")")))
                (if (< p 1)
                  (dg/request-animation-frame! frame)
                  (do (dg/remove-element robot)
                      (rf/dispatch [::enrich-event/open-menu-on-job token])))))]
      (dg/request-animation-frame! frame))))

(defn to-processes!
  "Fly a robot from `source-el` (the control that started the job) into the
   process list, opened on the job started with `token`."
  [source-el token]
  (when (shown (dg/element-by-id target-dom-id))
    (if (or (reduced-motion?) (not (shown source-el)))
      (rf/dispatch [::enrich-event/open-menu-on-job token])
      (fly! source-el token))))
